/**
 * Open a game — every club's games with processed tracking, the precondition
 * for running an intelligence pack.
 *
 * COLLAPSED BY DEFAULT and no org switching: staff work across clubs, and a
 * hundred-plus games listed flat is not a list anyone reads. A club's games
 * and its team badges are fetched when it is opened, not before, so the page
 * costs one request no matter how many clubs exist.
 *
 * Badges come from two places that already existed: the club's own logo_url
 * on the admin listing, and the per-club team-logo map, which returns every
 * team code appearing in that club's games — both sides of every match in one
 * request. A missing badge degrades to the team's code, never to a broken
 * image (components/ClubLogo).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ClubLogo } from '../components/ClubLogo'
import { ApiError } from '../lib/api'
import {
  getClubGames, getClubTeamBadges, getClubs,
  type CachedClub, type CachedGame,
} from '../lib/clubsCache'
import { pathSeg } from '../lib/urls'

import './insights-lab.css'

/** 'LIB@UT' -> ['LIB', 'UT']; anything else -> []. */
export function matchSides(gameCode: string): string[] {
  const parts = gameCode.split('@')
  if (parts.length !== 2) return []
  return parts.map((p) => (p.match(/^[A-Za-z]+/)?.[0] ?? p).toUpperCase())
}

type ClubState = {
  games: CachedGame[] | null
  badges: Map<string, string | null> | null
  error: string | null
}

export default function LabGames() {
  const navigate = useNavigate()
  const [clubs, setClubs] = useState<CachedClub[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<number | null>(null)
  const [state, setState] = useState<Record<number, ClubState>>({})
  const [filter, setFilter] = useState('')
  // The matrix's club scope (?club=), mirrored here so the accordion never
  // contradicts the scoped table above it. Read-only: the matrix's select
  // is the one writer of the parameter.
  const [params] = useSearchParams()
  const clubCode = (params.get('club') ?? '').trim().toUpperCase() || null
  useEffect(() => {
    getClubs()
      .then(setClubs)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  const load = useCallback((club: CachedClub) => {
    if (state[club.id]) return
    setState((s) => ({ ...s, [club.id]: { games: null, badges: null, error: null } }))
    Promise.all([getClubGames(club.id), getClubTeamBadges(club.id)])
      .then(([games, badges]) =>
        setState((s) => ({ ...s, [club.id]: { games, badges, error: null } })))
      .catch((e: unknown) =>
        setState((s) => ({
          ...s,
          [club.id]: {
            games: [], badges: null,
            error: e instanceof ApiError ? e.message : String(e),
          },
        })))
  }, [state])

  const toggle = useCallback((club: CachedClub) => {
    setOpen((cur) => (cur === club.id ? null : club.id))
    load(club)
  }, [load])

  // A scoped club opens itself: the scope already said "this club's games",
  // and a single collapsed row would answer that with a click for nothing.
  // Once per scope value, so collapsing it by hand is not fought.
  const autoOpened = useRef<string | null>(null)
  useEffect(() => {
    if (!clubCode || !clubs || autoOpened.current === clubCode) return
    const club = clubs.find((c) => c.code.toUpperCase() === clubCode)
    if (!club) return
    autoOpened.current = clubCode
    setOpen(club.id)
    load(club)
  }, [clubCode, clubs, load])

  const shown = useMemo(() => {
    if (!clubs) return []
    const scoped = clubCode
      ? clubs.filter((c) => c.code.toUpperCase() === clubCode)
      : clubs
    const q = filter.trim().toLowerCase()
    if (!q) return scoped
    return scoped.filter((c) =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q))
  }, [clubs, filter, clubCode])

  if (error) {
    return <div className="lab-error">Could not load clubs — {error}</div>
  }
  if (!clubs) return <p className="muted">Loading…</p>

  return (
    <>
    <section className="lab-games">
      <div className="lab-games__head">
        <div>
          <h2>Open a game</h2>
          <p className="lab-sub">
            Every club&apos;s games with processed tracking — the precondition
            for running an intelligence pack. No org switching needed.
          </p>
        </div>
        <div className="lab-games__tools">
          <span className="lab-games__count">
            {shown.length} of {clubs.length} clubs
          </span>
          <input type="search" placeholder="Filter clubs"
                 aria-label="Filter clubs"
                 value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
      </div>

      <ul className="lab-games__clubs">
        {shown.map((club) => {
          const isOpen = open === club.id
          const st = state[club.id]
          return (
            <li key={club.id} className="lab-club">
              <button type="button" className="lab-club__row"
                      aria-expanded={isOpen}
                      onClick={() => toggle(club)}>
                <span className={`lab-club__chevron${isOpen ? ' is-open' : ''}`}
                      aria-hidden="true">›</span>
                <ClubLogo code={club.code} url={club.logo_url} name={club.name}
                          size="sm" />
                <span className="lab-club__code mono">{club.code}</span>
                <span className="lab-club__name">{club.name}</span>
                <span className="lab-club__count mono">{club.games} games</span>
              </button>

              {isOpen && (
                <div className="lab-club__body">
                  {st?.error && (
                    <div className="lab-error">Could not load games — {st.error}</div>
                  )}
                  {!st?.games && !st?.error && <p className="muted">Loading games…</p>}
                  {st?.games?.length === 0 && !st.error && (
                    <div className="lab-empty">No games with processed tracking.</div>
                  )}
                  {st?.games && st.games.length > 0 && (
                    <ul className="lab-games__list">
                      {st.games.map((g) => (
                        <li key={g.game_code} className="lab-game lab-game--clickable"
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest('a')) return
                              navigate(`/manage-insights/game/${pathSeg(g.game_code)}`)
                            }}>
                          <span className="lab-game__badges">
                            {matchSides(g.game_code).map((side) => (
                              <ClubLogo key={side} code={side}
                                        url={st.badges?.get(side) ?? null}
                                        name={side} size="sm" />
                            ))}
                          </span>
                          <span className="lab-game__code mono">{g.game_code}</span>
                          <span className="lab-game__date">
                            {g.date ? g.date.slice(0, 10) : '—'}
                          </span>
                          <Link className="lab-game__open"
                                to={`/match-room/${pathSeg(g.game_code)}`}>
                            Video
                          </Link>
                          <Link className="lab-game__open"
                                to={`/manage-insights/game/${pathSeg(g.game_code)}`}>
                            Open
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {shown.length === 0 && clubCode && (
        <div className="lab-empty">No club matching {clubCode} here.</div>
      )}
    </section>
    </>
  )
}
